import { StyleSheet } from "@-/com.npmjs.react-native";
import { Text } from "@-/com.npmjs.react-native/text";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useSyncStatus } from "@-/dev.improve.haniverse.lib.sync-client";
import {
  getSyncAccessibilityLabel,
  getSyncDiagnosticsAccessibilityLabel,
} from "#get-sync-accessibility-label";

/**
 * Headless, release-safe publisher of the `actor-synced gen N` accessibility sentinel.
 *
 * It used to be surfaced only by a dev-only visual stamp, which rendered nothing in production
 * (`__DEV__` gate) — so on a staging/production RELEASE build (which is exactly what
 * the staging Maestro suite drives) the sentinel was entirely absent from the a11y tree, and any
 * flow that blocked on `actor-synced gen` silently fell through its optional wait and raced the
 * sync (observed: `review-card-back-to-review` tapping Learn before the review-schedule row
 * landed, so the resolver routed into a NEW character's lesson instead of the seeded due review).
 *
 * This component publishes ONLY the sync label to the accessibility tree, in every build. It must
 * stay VISUALLY unobtrusive without collapsing to a degenerate frame — a 1×1px / `fontSize:1` node
 * gets clipped to a zero-ish frame that Maestro's iOS accessibility snapshot culls (the first
 * attempt did exactly this and never appeared in the tree). So render real, laid-out text (normal
 * font size, its natural content box) but make it imperceptible: near-transparent color, pinned
 * flush to the very bottom edge, and `pointerEvents="none"` so it never intercepts a tab tap. The
 * text has a genuine accessibility frame, so `actor-synced gen N` is reliably queryable, while a
 * a near-zero-opacity glyph strip at the screen's bottom edge reads as blank to a human.
 */
export function SyncStatusSentinel() {
  const syncStatus = useSyncStatus();
  const syncAccessibilityLabel = getSyncAccessibilityLabel(syncStatus.initialPullGeneration);
  const [signalSeq] = useDeviceSetting("remoteChangeSignalSeq");
  const [caughtUpSeq] = useDeviceSetting("remoteChangeCaughtUpSeq");
  const [syncPushRegistration] = useDeviceSetting("syncPushRegistration");
  const syncDiagnosticsLabel = getSyncDiagnosticsAccessibilityLabel(syncStatus, {
    caughtUpSeq,
    isRegistered: syncPushRegistration !== null,
    signalSeq,
  });

  return (
    <>
      <Text
        accessibilityLabel={syncAccessibilityLabel}
        accessible
        importantForAccessibility="yes"
        pointerEvents="none"
        style={styles.sentinel}
      >
        {syncAccessibilityLabel}
      </Text>
      {/* A SEPARATE node so flows matching `actor-syncing` / `actor-synced gen N` are unaffected. */}
      <Text
        accessibilityLabel={syncDiagnosticsLabel}
        accessible
        importantForAccessibility="yes"
        pointerEvents="none"
        style={styles.diagnostics}
      >
        {syncDiagnosticsLabel}
      </Text>
    </>
  );
}

// Real laid-out text (so it has a genuine, queryable accessibility frame) that is invisible to a
// human: a small, transparent glyph run pinned flush to the bottom-left edge. Android's Release
// text renderer was still painting the transparent run as a dark strip on the Pixel onboarding
// surfaces, so retain a tiny non-zero opacity as a second drawing-level guard. The non-zero alpha
// preserves the real accessibility frame; `fontSize:1` / `width:1` would clip it to near-zero and
// iOS/Maestro would cull it from the hierarchy (the bug this replaces).
const styles = StyleSheet.create({
  diagnostics: {
    bottom: 0,
    color: "transparent",
    fontSize: 6,
    opacity: 0.001,
    position: "absolute",
    right: 0,
  },
  sentinel: {
    bottom: 0,
    color: "transparent",
    fontSize: 6,
    left: 0,
    opacity: 0.001,
    position: "absolute",
  },
});
